import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { extractErrorMessage } from '../../../api/errors';
import { marketplaceApi } from '../../../api/marketplace';
import { useAuth } from '../../../context/AuthContext';
import Button from '../../../components/ui/Button';
import Card from '../../../components/ui/Card';
import Input from '../../../components/ui/Input';
import { useCart } from './CartContext';

function formatCurrency(n) {
  return `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
}

export default function Cart() {
  const { cart, updateQuantity, removeItem, totals, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [delivery, setDelivery] = useState({
    delivery_contact_name: user?.full_name || user?.username || '',
    delivery_contact_phone: user?.phone || '',
    delivery_address: '',
    notes: '',
  });
  const [placing, setPlacing] = useState(false);
  const [error, setError] = useState('');

  const placeOrder = async (e) => {
    e.preventDefault();
    if (cart.items.length === 0) return;
    setError('');
    setPlacing(true);
    try {
      const order = await marketplaceApi.placeOrder({
        ...delivery,
        items: cart.items.map((i) => ({
          listing_id: i.listing_id,
          quantity: i.quantity,
        })),
      });
      clear();
      navigate(`/app/orders?placed=${order.id}`);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setPlacing(false);
    }
  };

  if (cart.items.length === 0) {
    return (
      <div className="flex flex-col gap-4">
        <h1 className="text-2xl font-semibold text-gray-900">Cart</h1>
        <Card className="p-8 text-center">
          <p className="text-sm text-gray-500">Your cart is empty.</p>
          <div className="mt-4">
            <Link to="/app/marketplace">
              <Button>Browse marketplace</Button>
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">Cart</h1>
        <p className="mt-1 text-sm text-gray-600">
          Sourcing from <span className="font-semibold">{cart.organisation_name}</span>. Items in a single
          order must come from one FPO.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card className="overflow-hidden">
         <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Listing', 'Unit price', 'Quantity', 'Line total', ''].map((h) => (
                  <th
                    key={h}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {cart.items.map((i) => (
                <tr key={i.listing_id}>
                  <td className="px-4 py-3 text-sm">
                    <p className="font-medium text-gray-900">{i.listing_title}</p>
                    <p className="text-xs text-gray-500">{i.crop_name}</p>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {formatCurrency(i.price_per_unit)} / {i.unit}
                  </td>
                  <td className="px-4 py-3">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={i.quantity}
                      onChange={(e) => updateQuantity(i.listing_id, e.target.value)}
                      className="w-24 rounded border border-gray-300 px-2 py-1 text-sm focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
                    />
                    <span className="ml-1 text-xs text-gray-500">{i.unit}</span>
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                    {formatCurrency(i.quantity * i.price_per_unit)}
                  </td>
                  <td className="px-4 py-3 text-right text-sm">
                    <button
                      type="button"
                      onClick={() => removeItem(i.listing_id)}
                      className="text-red-600 hover:text-red-700"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan={3} className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">
                  Subtotal
                </td>
                <td colSpan={2} className="px-4 py-3 text-base font-bold text-gray-900">
                  {formatCurrency(totals.subtotal)}
                </td>
              </tr>
            </tfoot>
          </table>
         </div>
        </Card>

        <Card className="p-6">
          <h2 className="text-base font-semibold text-gray-900">Delivery & checkout</h2>
          <p className="mt-1 text-xs text-gray-500">
            Payment is handled out-of-band with the FPO — the order tracks status; you settle directly.
          </p>
          <form className="mt-4 flex flex-col gap-3" onSubmit={placeOrder}>
            <Input
              label="Contact name"
              value={delivery.delivery_contact_name}
              onChange={(e) => setDelivery({ ...delivery, delivery_contact_name: e.target.value })}
              required
            />
            <Input
              label="Contact phone"
              type="tel"
              value={delivery.delivery_contact_phone}
              onChange={(e) => setDelivery({ ...delivery, delivery_contact_phone: e.target.value })}
              required
            />
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Delivery address</label>
              <textarea
                value={delivery.delivery_address}
                onChange={(e) => setDelivery({ ...delivery, delivery_address: e.target.value })}
                required
                rows={3}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-sm font-medium text-gray-700">Notes (optional)</label>
              <textarea
                value={delivery.notes}
                onChange={(e) => setDelivery({ ...delivery, notes: e.target.value })}
                rows={2}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-200"
                placeholder="Call before delivery, specific instructions…"
              />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <Button type="submit" size="lg" disabled={placing}>
              {placing ? 'Placing…' : `Place order · ${formatCurrency(totals.subtotal)}`}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
